import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Play } from 'lucide-react';
import { ApiError } from '../api/client';
import {
  getProduktyHraDetail,
  listProduktyHry,
  type ProduktyHraItem,
  type ProduktyPrehledStats,
} from '../api/rondo';
import { ServiceBar } from '../shell/ServiceBar';
import { useFeather } from '../shell/useFeather';
import { COUNTRY_NAME, TrendLineChart, dayFmt, dayShort, intFmt } from './ProduktyDetailShared';
import { Button } from '../components/arc/button/button';
import { Card } from '../components/arc/card/card';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { PageState } from '../components/arc/page-state/page-state';
import { Progress } from '../components/arc/progress/progress';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import './users-seznam.css';
import './produkty-prehled.css';
import './produkty-hry.css';

const GAME_LABELS: Record<string, string> = {
  bigQuiz: 'Big Quiz',
  rondoGame: 'Rondo hra',
  bubbleBreaker: 'Bubble Breaker',
  ticTacToe: 'Piškvorky',
  losy: 'Losy',
  losyPlus: 'Losy Plus',
  guessGame: 'Hádanka',
};

const LIMIT_OPTIONS = [
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '200', label: '200' },
  { value: 'all', label: 'vše' },
];

export function ProduktyHryPage() {
  const [selectedType, setSelectedType] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('type'),
  );

  useEffect(() => {
    const onPop = () => setSelectedType(new URLSearchParams(window.location.search).get('type'));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function openDetail(type: string) {
    const sp = new URLSearchParams(window.location.search);
    sp.set('type', type);
    window.history.pushState(null, '', `${window.location.pathname}?${sp.toString()}`);
    setSelectedType(type);
  }

  function backToList() {
    const sp = new URLSearchParams(window.location.search);
    sp.delete('type');
    const qs = sp.toString();
    window.history.pushState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
    setSelectedType(null);
  }

  if (selectedType) {
    return <HryDetail type={selectedType} onBack={backToList} />;
  }
  return <HryList onOpen={openDetail} />;
}

function HryList({ onOpen }: { onOpen: (type: string) => void }) {
  const [rows, setRows] = useState<ProduktyHraItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState('50');

  useEffect(() => {
    let cancelled = false;
    listProduktyHry()
      .then((items) => !cancelled && setRows(items))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const shown = useMemo(
    () => (limit === 'all' ? rows : rows.slice(0, Number(limit))),
    [rows, limit],
  );
  const totals = useMemo(() => rows.reduce((acc, game) => ({
    spins7d: acc.spins7d + game.spins7d,
    spins30d: acc.spins30d + game.spins30d,
    players: acc.players + game.avgUsersDay,
  }), { spins7d: 0, spins30d: 0, players: 0 }), [rows]);

  useFeather([shown]);
  const tableRows = shown.map((game) => ({ ...game, detail: game.gameType }));
  type TableRow = (typeof tableRows)[number];
  const columns: DataColumn<TableRow>[] = [
    { key: 'label', label: 'Hra', width: 215, render: (_, row) => <button type="button" className="rondo-game-link" onClick={() => onOpen(row.gameType)}><span className="rondo-game-avatar"><Play size={15} aria-hidden="true" /></span><span><strong>{row.label}</strong><small>{row.gameType}</small></span></button> },
    { key: 'spins7d', label: 'Spinů 7 dní', numeric: true, width: 115, render: (_, row) => intFmt(row.spins7d) },
    { key: 'spins30d', label: 'Spinů 30 dní', numeric: true, width: 125, render: (_, row) => intFmt(row.spins30d) },
    { key: 'avgUsersDay', label: 'Hráčů / den', numeric: true, width: 120, render: (_, row) => intFmt(row.avgUsersDay) },
    { key: 'cz', label: 'CZ', numeric: true, width: 95, render: (_, row) => intFmt(row.cz) },
    { key: 'sk', label: 'SK', numeric: true, width: 95, render: (_, row) => intFmt(row.sk) },
    { key: 'ro', label: 'RO', numeric: true, width: 95, render: (_, row) => intFmt(row.ro) },
    { key: 'detail', label: '', width: 45, sortable: false, render: (_, row) => <button type="button" className="rondo-arc-user-detail" aria-label={`Detail hry ${row.label}`} onClick={() => onOpen(row.gameType)}><ChevronRight size={16} aria-hidden="true" /></button> },
  ];

  return (
    <section className="rondo-arc-users-list rondo-arc-games p1-arc-scope"><header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">PRODUKTY</div><h1>Hry</h1><p>Aktivita her a hráčů za posledních 30 dní</p></div></header>
      <div className="rondo-arc-users-list-metrics">
        <MetricCard label="Hry v přehledu" value={rows.length} context="Bez soutěží" />
        <MetricCard label="Spinů za 7 dní" value={totals.spins7d} context="Všechny hry" />
        <MetricCard label="Spinů za 30 dní" value={totals.spins30d} context="Všechny hry" />
        <MetricCard label="Hráčů / den" value={totals.players} context="Součet her" />
      </div>
      <ServiceBar
        variant="seg"
        selects={[{ label: 'Limit', value: limit, onChange: setLimit, options: LIMIT_OPTIONS }]}
      />

      <div className="rondo-arc-users-list-table rondo-arc-games-table">
        {error && <p role="alert" className="rondo-arc-users-error">{error}</p>}
        {loading ? <p className="rondo-arc-users-loading">Načítám hry…</p> : <SortableDataTable rows={tableRows} columns={columns} rowKey="gameType" caption="Hry Ronda" emptyMessage="Žádná data." />}
      </div>

      <p className="rondo-games-count">{intFmt(shown.length)} zobrazených her{rows.length > shown.length ? ` z ${intFmt(rows.length)}` : ''}</p>
    </section>
  );
}

function HryDetail({ type, onBack }: { type: string; onBack: () => void }) {
  const [stats, setStats] = useState<ProduktyPrehledStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getProduktyHraDetail(type)
      .then((s) => !cancelled && setStats(s))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [type]);

  useFeather([stats]);

  const label = GAME_LABELS[type] ?? type;
  const todayLabel = stats?.latestSnapshot ? dayShort(stats.latestSnapshot) : 'dnes';

  return (
    <section className="rondo-arc-game-detail rondo-arc-products-overview p1-arc-scope">
      <Button variant="secondary" size="sm" onClick={onBack}>Zpět na hry</Button>
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">HRA</div><h1>{label}</h1><p>{type} · aktivita za posledních 30 dní</p></div></header>

      {loading ? (
        <PageState title="Načítám hru" loading />
      ) : error ? (
        <PageState title="Hru se nepodařilo načíst" message={error} />
      ) : !stats ? null : (
        <>
          <div className="rondo-arc-users-list-metrics">
            <MetricCard label="Dnes" value={stats.playsToday} context={`${todayLabel} · spinů`} />
            <MetricCard label="Posledních 7 dní" value={stats.plays7d} context="Spinů" />
            <MetricCard label="Posledních 30 dní" value={stats.plays30d} context="Spinů" />
            <MetricCard label="Hráčů / den" value={stats.avgUniqueUsersDay30d} context="Unikátní hráči, průměr" />
          </div>
          <GameDetailInsights stats={stats} />
        </>
      )}
    </section>
  );
}

function GameDetailInsights({ stats }: { stats: ProduktyPrehledStats }) {
  const maxCountry = Math.max(1, ...stats.byCountry.map((country) => country.plays));
  const dailyRows = stats.detail14d.map((day) => ({ ...day }));
  const dailyColumns: DataColumn<(typeof dailyRows)[number]>[] = [
    { key: 'date', label: 'Období', width: 150, render: (_, row) => dayFmt(row.date) },
    { key: 'cz', label: 'Česko', numeric: true, render: (_, row) => intFmt(row.cz) },
    { key: 'sk', label: 'Slovensko', numeric: true, render: (_, row) => intFmt(row.sk) },
    { key: 'ro', label: 'Rumunsko', numeric: true, render: (_, row) => intFmt(row.ro) },
    { key: 'total', label: 'Celkem', numeric: true, render: (_, row) => <strong>{intFmt(row.total)}</strong> },
  ];
  return (
    <>
      <div className="rondo-arc-product-insights">
        <Card title="Denní aktivita" description="CZ, SK a RO · posledních 30 dní" className="rondo-arc-product-chart-card">
          <div className="rondo-arc-product-chart">{stats.trend30d.length ? <TrendLineChart points={stats.trend30d} /> : <div className="product-empty">Žádná data.</div>}</div>
          <div className="rondo-arc-product-legend"><span className="cz"><i/>Česko</span><span className="sk"><i/>Slovensko</span><span className="ro"><i/>Rumunsko</span></div>
        </Card>
        <Card title="Aktivita podle země" description={stats.latestSnapshot ? `Poslední data ${dayFmt(stats.latestSnapshot)}` : 'Posledních 30 dní'} className="rondo-arc-product-markets-card">
          <div className="rondo-arc-product-markets">{stats.byCountry.map((country) => <div className="rondo-arc-product-market" key={country.country}>
            <div><span className="rondo-arc-product-market-code">{country.country}</span><div><strong>{COUNTRY_NAME[country.country] ?? country.country}</strong><small>{intFmt(country.uniqueUsers)} hráčů</small></div><strong>{intFmt(country.plays)} <small>spinů</small></strong></div>
            <Progress value={country.plays} max={maxCountry} label={`${COUNTRY_NAME[country.country] ?? country.country}: ${intFmt(country.plays)} spinů`} />
          </div>)}</div>
        </Card>
      </div>
      <Card title="Denní vývoj podle trhu" description="Posledních 14 dní" className="rondo-arc-product-daily-card rondo-arc-game-daily-card"><SortableDataTable rows={dailyRows} columns={dailyColumns} rowKey="date" caption="Denní aktivita hry podle trhu" emptyMessage="Žádná data." /></Card>
    </>
  );
}
